/* eslint-disable no-empty */
import {
  BasketballEventDetails,
  BasketballEventReplyAnswer,
  BasketballEventUserData,
  BasketballEventUserRolePlayerCoach,
  BasketballEventUserRoleType,
  EventComment,
  EventUserReply,
  RequestBaseUpdateEventReply,
  RequestBasketballPostEvent,
  RequestBasketballPostEventComment,
  ResponseBaseUpdateEventReply,
  ResponseBasketballGetEventComments,
  ResponseBasketballGetEventRequestData,
  ResponseBasketballGetEventSelectOptions,
} from '@hefe/models';
import { createApi } from '@reduxjs/toolkit/query/react';
import API from '../../../api/api';

export const eventsApi = createApi({
  reducerPath: 'eventsApi',
  baseQuery: API.baseQuery,
  tagTypes: ['BasketballEventRequestData'],
  endpoints: (builder) => ({
    /**************************************************************************
     *                              ARTICLES
     *************************************************************************/
    fetchClubBasketballEvents: builder.query<BasketballEventDetails[], void>({
      query: (request) => `basketball/events`,
    }),
    fetchBasketballEvent: builder.query<BasketballEventDetails, number>({
      query: (request) => `basketball/events/${request}`,
    }),
    fetchBasketballEventRequestData: builder.query<
      ResponseBasketballGetEventRequestData,
      number
    >({
      query: (request) => `basketball/events/${request}/request-data`,
      providesTags: (result, error, id) => [
        { type: 'BasketballEventRequestData', id: result?.id },
      ],
    }),
    fetchBasketballEventSelectOptions: builder.query<
      ResponseBasketballGetEventSelectOptions,
      {
        eventId?: number;
        teamId?: number;
      }
    >({
      query: (request) => {
        const params = new URLSearchParams();
        if (request.eventId)
          params.append('event-id', request.eventId.toString());
        if (request.teamId) params.append('team-id', request.teamId.toString());
        return `basketball/events/select-options?${params.toString()}`;
      },
    }),
    saveBasketballEvent: builder.mutation<
      BasketballEventDetails,
      RequestBasketballPostEvent
    >({
      query: (request) => ({
        url: 'basketball/events',
        method: 'POST',
        body: request,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            eventsApi.util.updateQueryData(
              'fetchBasketballEvent',
              responseData?.id,
              (draft) => responseData
            )
          );

          if (responseData?.createable_type === 'App\\Models\\Base\\Club') {
            dispatch(
              eventsApi.util.updateQueryData(
                'fetchClubBasketballEvents',
                undefined,
                (draft) => {
                  if (request.id) {
                    return draft.map((event) =>
                      event.id === responseData?.id ? responseData : event
                    );
                  } else {
                    const allEvents = [...draft, responseData];
                    // Sort by date
                    return allEvents.sort(
                      (a, b) =>
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                    );
                  }
                }
              )
            );
          }
        } catch (error: unknown) {}
      },
      invalidatesTags: (result) => {
        return [
          {
            type: 'BasketballEventRequestData',
            id: result?.id,
          },
        ];
      },
    }),
    updateEventReply: builder.mutation<
      ResponseBaseUpdateEventReply,
      { eventId: number; request: RequestBaseUpdateEventReply }
    >({
      query: ({ eventId, request }) => ({
        url: `events/${eventId}/reply`,
        method: 'PUT',
        body: request,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            eventsApi.util.updateQueryData(
              'fetchBasketballEvent',
              request?.eventId ?? 0,
              (draft) => {
                if (draft && draft.replies) {
                  const oldReplyType =
                    responseData?.old_reply != null
                      ? responseData?.old_reply ===
                        BasketballEventReplyAnswer.COMMITMENT
                        ? 'yes'
                        : 'no'
                      : 'unknown';
                  const newReplyType =
                    responseData?.reply != null
                      ? responseData?.reply ===
                        BasketballEventReplyAnswer.COMMITMENT
                        ? 'yes'
                        : 'no'
                      : 'unknown';

                  const user = draft?.replies[oldReplyType].find(
                    (u: EventUserReply) => u.id === request?.request?.user_id
                  );

                  // Remove the old reply from the old reply type
                  draft.replies[oldReplyType] = draft.replies[
                    oldReplyType
                  ]?.filter((reply) => reply.id !== request?.request?.user_id);

                  // Add the new reply to the new reply type
                  if (draft.replies[newReplyType]) {
                    if (user && user.id !== undefined) {
                      draft.replies[newReplyType].push({
                        ...user,
                        reply: responseData,
                      });
                    }
                  }
                }

                if (draft && draft.user_data) {
                  for (const user of draft.user_data as BasketballEventUserData[]) {
                    if (
                      responseData.repliable_type ===
                        'App\\Models\\Base\\User' &&
                      responseData.repliable_id === user?.user?.id
                    ) {
                      for (const role of user.roles as BasketballEventUserRolePlayerCoach[]) {
                        if (
                          role.type === BasketballEventUserRoleType.PARTICIPANT
                        ) {
                          role.reply = {
                            require_reply_reason: false,
                            reply: responseData.reply,
                          };
                        }
                      }
                    }
                  }
                }
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
    // deleteEvent
    deleteEvent: builder.mutation<void, number>({
      query: (eventId) => ({
        url: `events/${eventId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(request, { dispatch }) {
        try {
          dispatch(
            eventsApi.util.updateQueryData(
              'fetchClubBasketballEvents',
              undefined,
              (draft) => draft.filter((event) => event.id !== request)
            )
          );
        } catch (error: unknown) {}
      },
    }),
    /*************************************************************************
     *                      Event Comments
     *************************************************************************/
    fetchBasketballEventComments: builder.query<
      ResponseBasketballGetEventComments,
      { eventId: number; page: number }
    >({
      query: ({ eventId, page }) =>
        `basketball/events/${eventId}/comments?page=${page}`,
      serializeQueryArgs: ({ queryArgs }) => {
        const { eventId } = queryArgs;
        return { eventId }; // Gruppiert die Kommentare nur nach Event
      },
      merge: (currentCache, newItems) => {
        if (currentCache?.data) {
          if (newItems?.current_page === 1) {
            return newItems;
          } else {
            return {
              ...currentCache,
              ...newItems,
              data: [...currentCache.data, ...newItems.data],
            };
          }
        }
        return newItems;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
    }),
    createBasketballEventComment: builder.mutation<
      EventComment,
      { eventId: number; request: RequestBasketballPostEventComment }
    >({
      query: (request) => ({
        url: `basketball/events/${request.eventId}/comments`,
        method: 'POST',
        body: request.request,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            eventsApi.util.updateQueryData(
              'fetchBasketballEventComments',
              { eventId: request.eventId, page: 1 },
              (draft) => {
                if (draft?.data) {
                  draft.data.unshift(responseData);
                }
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  reducerPath,
  reducer,
  middleware,
  useFetchClubBasketballEventsQuery,
  useFetchBasketballEventQuery,
  useFetchBasketballEventRequestDataQuery,
  useFetchBasketballEventSelectOptionsQuery,
  useSaveBasketballEventMutation,
  useUpdateEventReplyMutation,
  useDeleteEventMutation,
  useLazyFetchBasketballEventCommentsQuery,
  useCreateBasketballEventCommentMutation,
} = eventsApi;
